import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ListChecks,
  CheckCircle2,
  Circle,
  Clock,
  BookmarkPlus,
  Bookmark,
  StickyNote,
  ChevronRight,
  ChevronDown,
  Trophy,
  Target,
  Play,
  Pause,
  FileText,
  Zap,
  ClipboardList,
  Download,
  Loader2,
  HelpCircle,
  CheckCircle,
  XCircle,
  Network,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";
import ConceptMapDialog from './ConceptMapDialog';

export default function EnhancedCourseOutline({ courseId, courseTitle, topics }) {
  const [topicProgress, setTopicProgress] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  const [notes, setNotes] = useState({});
  const [currentNote, setCurrentNote] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [progressExpanded, setProgressExpanded] = useState(true);
  const [topicsExpanded, setTopicsExpanded] = useState(true);
  const [stats, setStats] = useState({
    total_topics: 0,
    completed_topics: 0,
    completion_percentage: 0,
    total_study_time_minutes: 0
  });
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [courseMaterials, setCourseMaterials] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [assignmentStats, setAssignmentStats] = useState({
    total_assignments: 0,
    completed_assignments: 0,
    completion_percentage: 0
  });
  const [summaryLoading, setSummaryLoading] = useState({});
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [worksheetLoading, setWorksheetLoading] = useState({});
  const [worksheetDialogOpen, setWorksheetDialogOpen] = useState(false);
  const [currentWorksheet, setCurrentWorksheet] = useState(null);
  const [quizLoading, setQuizLoading] = useState({});
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [conceptMapLoading, setConceptMapLoading] = useState({});
  const [conceptMapDialogOpen, setConceptMapDialogOpen] = useState(false);
  const [currentConceptMap, setCurrentConceptMap] = useState(null);
  const [currentConceptMapTopic, setCurrentConceptMapTopic] = useState('');

  const token = localStorage.getItem('faheem_token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Load progress data
  const loadProgressData = useCallback(async () => {
    if (!token) return;

    try {
      // Load course details from database
      const courseRes = await axios.get(`${BACKEND_URL}/api/courses/`);
      if (courseRes.data && courseRes.data.courses) {
        const course = courseRes.data.courses.find(c => c.id === courseId);
        if (course) {
          setCourseDetails(course);
          setCourseMaterials(course.course_materials);
        }
      }

      // Load topic progress
      const progressRes = await axios.get(
        `${BACKEND_URL}/api/course-progress/topics/${courseId}?access_token=${token}`
      );

      if (progressRes.data) {
        const progressMap = {};
        progressRes.data.topics.forEach(topic => {
          progressMap[topic.topic_id] = topic;
        });
        setTopicProgress(progressMap);
        setStats(progressRes.data.stats);
      }

      // Load bookmarks
      const bookmarksRes = await axios.get(
        `${BACKEND_URL}/api/course-progress/bookmarks/${courseId}?access_token=${token}`
      );

      if (bookmarksRes.data) {
        const bookmarkSet = new Set(bookmarksRes.data.bookmarks.map(b => b.topic_id));
        setBookmarks(bookmarkSet);
      }

      // Load notes
      const notesRes = await axios.get(
        `${BACKEND_URL}/api/course-progress/notes/${courseId}?access_token=${token}`
      );

      if (notesRes.data) {
        const notesMap = {};
        notesRes.data.notes.forEach(note => {
          if (!notesMap[note.topic_id]) {
            notesMap[note.topic_id] = [];
          }
          notesMap[note.topic_id].push(note);
        });
        setNotes(notesMap);
      }

      // Load assignment stats
      const assignmentsRes = await axios.get(
        `${BACKEND_URL}/api/assignments/stats/${courseId}?access_token=${token}`
      );

      if (assignmentsRes.data) {
        setAssignmentStats(assignmentsRes.data);
      }
    } catch (error) {
      console.error('Failed to load progress data:', error);
    }
  }, [courseId, token, BACKEND_URL]);

  useEffect(() => {
    loadProgressData();
  }, [loadProgressData]);

  // Timer management
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  const startTimer = (topicId) => {
    if (!isTimerRunning) {
      setIsTimerRunning(true);
      setSelectedTopic(topicId);
      setTimerStartTime(Date.now());
      setCurrentTimeSeconds(0);
      
      // Update every second for accurate display
      const interval = setInterval(() => {
        setCurrentTimeSeconds(prev => prev + 1);
      }, 1000); // Increment every second
      setTimerInterval(interval);
    }
  };

  const stopTimer = async () => {
    if (isTimerRunning && timerInterval) {
      clearInterval(timerInterval);
      setIsTimerRunning(false);

      // Calculate total elapsed time in seconds using actual timestamps
      const elapsedSeconds = timerStartTime ? Math.floor((Date.now() - timerStartTime) / 1000) : currentTimeSeconds;
      const elapsedMinutes = Math.ceil(elapsedSeconds / 60); // Round up to nearest minute

      if (elapsedMinutes > 0 && selectedTopic) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/course-progress/topics/update-time?access_token=${token}`,
            null,
            {
              params: {
                course_id: courseId,
                topic_id: selectedTopic,
                minutes: elapsedMinutes
              }
            }
          );
          
          const hours = Math.floor(elapsedMinutes / 60);
          const mins = elapsedMinutes % 60;
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          
          toast.success(`Logged ${timeStr} of study time`);
        } catch (error) {
          console.error('Failed to update time:', error);
          toast.error('Failed to save study time');
        }
      }

      setCurrentTimeSeconds(0);
      setTimerStartTime(null);
      setSelectedTopic(null);
      loadProgressData();
    }
  };

  const toggleTopicCompletion = async (topicId, topicTitle, currentStatus) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/course-progress/topics/toggle?access_token=${token}`,
        {
          course_id: courseId,
          topic_id: topicId,
          topic_title: topicTitle,
          is_completed: !currentStatus,
          time_spent_minutes: 0
        }
      );

      if (response.data) {
        toast.success(!currentStatus ? 'Topic marked as completed!' : 'Topic marked as incomplete');
        loadProgressData();
      }
    } catch (error) {
      console.error('Failed to toggle topic:', error);
      toast.error('Failed to update topic status');
    }
  };

  const toggleBookmark = async (topicId, topicTitle) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/course-progress/bookmarks?access_token=${token}`,
        {
          course_id: courseId,
          topic_id: topicId,
          topic_title: topicTitle
        }
      );

      if (response.data) {
        toast.success(response.data.message);
        loadProgressData();
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Failed to update bookmark');
    }
  };

  const saveNote = async (topicId) => {
    if (!currentNote.trim()) return;

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/course-progress/notes?access_token=${token}`,
        {
          course_id: courseId,
          topic_id: topicId,
          note_content: currentNote
        }
      );

      if (response.data) {
        toast.success('Note saved!');
        setCurrentNote('');
        loadProgressData();
      }
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    }
  };

  const generateSummary = async (topicName, topicDescription) => {
    const topicKey = topicName;
    setSummaryLoading(prev => ({ ...prev, [topicKey]: true }));
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/topic-summary/generate`,
        {
          course_title: courseTitle,
          topic_name: topicName,
          topic_description: topicDescription || null
        }
      );

      if (response.data) {
        setCurrentSummary({
          topicName: topicName,
          summary: response.data.summary,
          courseTitle: courseTitle
        });
        setSummaryDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to generate summary:', error);
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setSummaryLoading(prev => ({ ...prev, [topicKey]: false }));
    }
  };

  const downloadSummary = () => {
    if (!currentSummary) return;
    
    const content = `
================================================================================
                            TOPIC SUMMARY
================================================================================

Course: ${currentSummary.courseTitle}
Topic: ${currentSummary.topicName}

================================================================================

${currentSummary.summary}

================================================================================
                        Generated by FaheemGPT
================================================================================
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = currentSummary.topicName.replace(/[^a-zA-Z0-9\s-_]/g, '_');
    a.download = `${safeName}_Summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Summary downloaded!');
  };

  const generateWorksheet = async (topicName, topicDescription) => {
    const topicKey = topicName;
    setWorksheetLoading(prev => ({ ...prev, [topicKey]: true }));
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/topic-worksheet/generate`,
        {
          course_title: courseTitle,
          topic_name: topicName,
          topic_description: topicDescription || null,
          difficulty_level: "mixed"
        }
      );

      if (response.data) {
        setCurrentWorksheet({
          topicName: topicName,
          worksheet: response.data.worksheet,
          courseTitle: courseTitle
        });
        setWorksheetDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to generate worksheet:', error);
      toast.error('Failed to generate worksheet. Please try again.');
    } finally {
      setWorksheetLoading(prev => ({ ...prev, [topicKey]: false }));
    }
  };

  const downloadWorksheet = () => {
    if (!currentWorksheet) return;
    
    const content = `
================================================================================
                            PRACTICE WORKSHEET
================================================================================

Course: ${currentWorksheet.courseTitle}
Topic: ${currentWorksheet.topicName}

================================================================================

${currentWorksheet.worksheet}

================================================================================
                        Generated by FaheemGPT
                    Practice Makes Perfect! 📚
================================================================================
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = currentWorksheet.topicName.replace(/[^a-zA-Z0-9\s-_]/g, '_');
    a.download = `${safeName}_Worksheet.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Worksheet downloaded!');
  };

  const generateQuiz = async (topicId, topicName, topicDescription) => {
    const topicKey = topicName;
    setQuizLoading(prev => ({ ...prev, [topicKey]: true }));
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/topic-quiz/generate`,
        {
          course_id: courseId,
          course_title: courseTitle,
          topic_id: topicId,
          topic_name: topicName,
          topic_description: topicDescription || null,
          num_questions: 10
        }
      );

      if (response.data) {
        setCurrentQuiz({
          ...response.data,
          topicId: topicId
        });
        setQuizAnswers({});
        setQuizSubmitted(false);
        setQuizResults(null);
        setQuizDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to generate quiz:', error);
      toast.error('Failed to generate quiz. Please try again.');
    } finally {
      setQuizLoading(prev => ({ ...prev, [topicKey]: false }));
    }
  };

  const handleQuizAnswer = (questionIndex, answerValue) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answerValue
    }));
  };

  const handleOpenEndedAnswer = (questionIndex, textValue) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: textValue
    }));
  };

  const submitQuiz = async () => {
    if (!currentQuiz || quizSubmitting) return;
    
    // Check if all questions are answered (including open-ended with non-empty text)
    const unanswered = currentQuiz.questions.filter((q, idx) => {
      const answer = quizAnswers[idx];
      const isOpenEnded = q.question_type === 'open_ended' || (q.options && q.options.length === 0);
      if (isOpenEnded) {
        return !answer || (typeof answer === 'string' && answer.trim() === '');
      }
      return answer === undefined;
    });
    
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} question(s) remaining.`);
      return;
    }
    
    setQuizSubmitting(true);
    
    try {
      const answersArray = currentQuiz.questions.map((_, idx) => quizAnswers[idx]);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/topic-quiz/submit?access_token=${token}`,
        {
          quiz_id: currentQuiz.quiz_id,
          course_id: courseId,
          topic_id: currentQuiz.topicId,
          topic_name: currentQuiz.topic_name,
          answers: answersArray,
          questions: currentQuiz.questions.map(q => ({
            question: q.question,
            options: q.options || [],
            correct_answer: q.correct_answer,
            question_type: q.question_type || 'multiple_choice'
          }))
        }
      );

      if (response.data) {
        setQuizResults(response.data);
        setQuizSubmitted(true);
        
        if (response.data.percentage >= 80) {
          toast.success(`Excellent! You scored ${response.data.percentage}%! 🎉`);
        } else if (response.data.percentage >= 60) {
          toast.success(`Good job! You scored ${response.data.percentage}%`);
        } else {
          toast.info(`You scored ${response.data.percentage}%. Keep practicing!`);
        }
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const retakeQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResults(null);
  };

  const closeQuizDialog = () => {
    setQuizDialogOpen(false);
    setCurrentQuiz(null);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResults(null);
  };

  const generateConceptMap = async (topicName, topicDescription) => {
    const topicKey = topicName;
    setConceptMapLoading(prev => ({ ...prev, [topicKey]: true }));
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/topic-concept-map/generate`,
        {
          topic_name: topicName,
          topic_description: topicDescription || null,
          course_id: courseId
        },
        { params: { access_token: token } }
      );

      if (response.data) {
        setCurrentConceptMap(response.data);
        setCurrentConceptMapTopic(topicName);
        setConceptMapDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to generate concept map:', error);
      toast.error('Failed to generate concept map. Please try again.');
    } finally {
      setConceptMapLoading(prev => ({ ...prev, [topicKey]: false }));
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const toggleTopicExpanded = (topicId) => {
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
      } else {
        newSet.add(topicId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-4">
      {/* Progress Summary Card - Collapsible */}
      <Card className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 border-0 shadow-xl">
        {/* Clickable Header to Collapse/Expand */}
        <button 
          onClick={() => setProgressExpanded(!progressExpanded)}
          className="w-full"
        >
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
        </div>
        
        <CardHeader className="pb-3 pt-4 px-4 relative z-10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
              <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Trophy className="w-4 h-4 text-yellow-300" />
              </div>
              Your Progress
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                <span className="text-xs font-bold text-white">Level {Math.floor(stats.completion_percentage / 20) + 1}</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-white/80 transition-transform ${progressExpanded ? '' : '-rotate-90'}`} />
            </div>
          </div>
        </CardHeader>
        </button>
        
        {progressExpanded && (
        <CardContent className="px-4 pb-4 relative z-10">
          {/* Two Circular Progress Indicators */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Topics Progress */}
            <div className="flex flex-col items-center">
              <div className="relative w-20 h-20 mb-1">
                {/* Background Circle */}
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="6"
                    fill="none"
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="white"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 35}`}
                    strokeDashoffset={`${2 * Math.PI * 35 * (1 - stats.completion_percentage / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-white">{stats.completion_percentage}%</span>
                </div>
              </div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide text-center">Topics</span>
            </div>

            {/* Assignments Progress */}
            <div className="flex flex-col items-center">
              <div className="relative w-20 h-20 mb-1">
                {/* Background Circle */}
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="6"
                    fill="none"
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="white"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 35}`}
                    strokeDashoffset={`${2 * Math.PI * 35 * (1 - assignmentStats.completion_percentage / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-white">{assignmentStats.completion_percentage}%</span>
                </div>
              </div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide text-center">Assignments</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2">
            {/* Topics Completed */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <div className="flex items-center justify-center mb-1">
                <Target className="w-3 h-3 text-green-300" />
              </div>
              <div className="text-sm font-bold text-white">{stats.completed_topics}/{stats.total_topics}</div>
              <div className="text-[8px] text-slate-400 uppercase tracking-wide">Topics</div>
            </div>

            {/* Assignments Completed */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <div className="flex items-center justify-center mb-1">
                <ClipboardList className="w-3 h-3 text-purple-300" />
              </div>
              <div className="text-sm font-bold text-white">{assignmentStats.completed_assignments}/{assignmentStats.total_assignments}</div>
              <div className="text-[8px] text-slate-400 uppercase tracking-wide">Tasks</div>
            </div>

            {/* Study Time */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <div className="flex items-center justify-center mb-1">
                <Clock className="w-3 h-3 text-orange-300" />
              </div>
              <div className="text-sm font-bold text-white">{formatTime(stats.total_study_time_minutes)}</div>
              <div className="text-[8px] text-slate-400 uppercase tracking-wide">Time</div>
            </div>

            {/* Overall Rate */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <div className="flex items-center justify-center mb-1">
                <Zap className="w-3 h-3 text-yellow-300" />
              </div>
              <div className="text-sm font-bold text-white">
                {Math.round(((stats.completion_percentage + assignmentStats.completion_percentage) / 2))}%
              </div>
              <div className="text-[8px] text-slate-400 uppercase tracking-wide">Overall</div>
            </div>
          </div>

          {/* Progress Message */}
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-xs text-center text-slate-300">
              {stats.completion_percentage === 0 && assignmentStats.completion_percentage === 0 && "🚀 Start your learning journey!"}
              {(stats.completion_percentage > 0 || assignmentStats.completion_percentage > 0) && 
               (stats.completion_percentage + assignmentStats.completion_percentage) / 2 < 25 && "💪 Great start! Keep going!"}
              {(stats.completion_percentage + assignmentStats.completion_percentage) / 2 >= 25 && 
               (stats.completion_percentage + assignmentStats.completion_percentage) / 2 < 50 && "🔥 You're making progress!"}
              {(stats.completion_percentage + assignmentStats.completion_percentage) / 2 >= 50 && 
               (stats.completion_percentage + assignmentStats.completion_percentage) / 2 < 75 && "⭐ Halfway there! Amazing!"}
              {(stats.completion_percentage + assignmentStats.completion_percentage) / 2 >= 75 && 
               (stats.completion_percentage + assignmentStats.completion_percentage) / 2 < 100 && "🎯 Almost done! Push harder!"}
              {stats.completion_percentage === 100 && assignmentStats.completion_percentage === 100 && "🏆 Course completed! Excellent work!"}
            </p>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Course Materials */}
      {courseMaterials && (
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-600" />
              Course Materials
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <a
              href={courseMaterials}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-2 bg-white rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                  <FileText className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-900">Course Textbook</p>
                  <p className="text-[10px] text-slate-500">PDF Document</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 transition-colors" />
            </a>
          </CardContent>
        </Card>
      )}

      {/* Study Timer Card - Compact */}
      {isTimerRunning && (
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-orange-600 animate-pulse" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-900">Studying Now</p>
                  <p className="text-[10px] text-slate-600 font-mono">
                    {Math.floor(currentTimeSeconds / 60)}:{String(currentTimeSeconds % 60).padStart(2, '0')}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={stopTimer}
                className="border-orange-300 hover:bg-orange-100 h-7 text-xs"
              >
                <Pause className="w-3 h-3 mr-1" />
                Stop
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Course Outline with Progress Tracking - Collapsible, Dark Blue Theme */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <button 
          onClick={() => setTopicsExpanded(!topicsExpanded)}
          className="w-full"
        >
          <CardHeader className="pb-3 pt-4 px-5 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold flex items-center gap-2.5 text-slate-900">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                  <ListChecks className="w-4 h-4 text-white" />
                </div>
                Course Topics
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs px-2.5 py-1 bg-slate-100 text-slate-600">
                  {topics.length} Topics
                </Badge>
                <Badge className="text-xs px-2.5 py-1 bg-slate-900 text-white hover:bg-slate-800">
                  {stats.completed_topics} Completed
                </Badge>
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${topicsExpanded ? '' : '-rotate-90'}`} />
              </div>
            </div>
          </CardHeader>
        </button>
        {topicsExpanded && (
        <CardContent className="px-5 py-4">
          <div className="space-y-3">
            {topics.map((topic, index) => {
              const topicId = `topic-${index + 1}`;
              const isCompleted = topicProgress[topicId]?.is_completed || false;
              const isBookmarked = bookmarks.has(topicId);
              const topicNotes = notes[topicId] || [];
              const timeSpent = topicProgress[topicId]?.time_spent_minutes || 0;
              const isExpanded = expandedTopics.has(topicId);
              
              // Handle both string topics and object topics with name/description
              const topicName = typeof topic === 'object' ? topic.name : topic;
              const topicDescription = typeof topic === 'object' ? topic.description : null;

              return (
                <div 
                  key={index} 
                  className={`rounded-xl border-2 transition-all duration-200 ${
                    isCompleted 
                      ? 'border-green-200 bg-green-50/50' 
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  {/* Topic Header - Clickable to expand */}
                  <button 
                    onClick={() => toggleTopicExpanded(topicId)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-start gap-3">
                      {/* Topic Number & Status */}
                      <div className="flex flex-col items-center gap-1.5">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                          isCompleted 
                            ? 'bg-green-500 text-white' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            index + 1
                          )}
                        </div>
                      </div>
                      
                      {/* Topic Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`text-sm font-semibold ${
                            isCompleted ? 'text-green-700' : 'text-slate-900'
                          }`}>
                            {topicName}
                          </h3>
                          {isBookmarked && (
                            <Bookmark className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                          )}
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ml-auto ${isExpanded ? '' : '-rotate-90'}`} />
                        </div>
                        
                        {/* Topic Stats - Always visible */}
                        <div className="flex items-center gap-3 text-xs">
                          {timeSpent > 0 && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Clock className="w-3 h-3" />
                              {formatTime(timeSpent)} studied
                            </span>
                          )}
                          {topicNotes.length > 0 && (
                            <span className="flex items-center gap-1 text-slate-600">
                              <StickyNote className="w-3 h-3" />
                              {topicNotes.length} notes
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-green-600 font-medium">Completed</span>
                          )}
                        </div>
                      </div>

                      {/* Mark Complete Button */}
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTopicCompletion(topicId, topicName, isCompleted);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                          isCompleted 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {isCompleted ? 'Completed' : 'Mark Done'}
                      </div>
                    </div>
                  </button>

                  {/* Expandable Content */}
                  {isExpanded && (
                  <>
                  {/* Topic Description */}
                  {topicDescription && (
                    <div className="px-4 pb-3">
                      <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-100">
                        {topicDescription}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons - Two Rows: Study Tools and AI Tools */}
                  <div className="px-4 pb-4 pt-0 space-y-3">
                    {/* Study Tools Row */}
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-200 px-2 py-1 rounded">
                          Study Tools
                        </span>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => !isTimerRunning ? startTimer(topicId) : toast.info('Timer already running')}
                          disabled={isTimerRunning && selectedTopic !== topicId}
                          className="text-xs h-8 px-3 bg-white border-slate-300 hover:border-slate-900 hover:bg-slate-100"
                        >
                          <Play className="w-3.5 h-3.5 mr-1.5" />
                          Timer
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleBookmark(topicId, topicName)}
                          className={`text-xs h-8 px-3 bg-white border-slate-300 ${
                            isBookmarked ? 'border-slate-700 bg-slate-100 text-slate-900' : 'hover:border-slate-900 hover:bg-slate-100'
                          }`}
                        >
                          {isBookmarked ? (
                            <>
                              <Bookmark className="w-3.5 h-3.5 mr-1.5 fill-current" />
                              Saved
                            </>
                          ) : (
                            <>
                              <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
                              Save
                            </>
                          )}
                        </Button>

                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className={`text-xs h-8 px-3 bg-white border-slate-300 ${
                                topicNotes.length > 0 ? 'border-slate-700 bg-slate-100 text-slate-900' : 'hover:border-slate-900 hover:bg-slate-100'
                              }`}
                            >
                              <StickyNote className="w-3.5 h-3.5 mr-1.5" />
                              Notes {topicNotes.length > 0 && `(${topicNotes.length})`}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white">
                            {/* Header Section - Clean Design */}
                            <div className="relative bg-white px-6 py-5 border-b border-slate-200">
                              <DialogHeader>
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <StickyNote className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div className="flex-1">
                                    <DialogTitle className="text-slate-900 text-lg font-bold mb-1">
                                      📝 Your Study Notes
                                    </DialogTitle>
                                    <DialogDescription className="text-slate-600 text-sm">
                                      {topicName}
                                    </DialogDescription>
                                  </div>
                                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                                    {topicNotes.length} {topicNotes.length === 1 ? 'Note' : 'Notes'}
                                  </Badge>
                                </div>
                              </DialogHeader>
                            </div>

                            {/* Content Section - White Background */}
                            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-white">
                              {/* Existing Notes */}
                              {topicNotes.length > 0 ? (
                                <div className="space-y-3">
                                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                                    Previous Notes
                                  </h3>
                                  <div className="space-y-3">
                                    {topicNotes.map((note, idx) => (
                                      <div 
                                        key={note.id} 
                                        className="group relative bg-white rounded-lg p-4 border-2 border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200"
                                      >
                                        {/* Note Number Badge */}
                                        <div className="absolute -top-3 -left-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                                          {idx + 1}
                                        </div>
                                        
                                        {/* Note Content */}
                                        <div className="ml-2">
                                          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                                            {note.note_content}
                                          </p>
                                          
                                          {/* Note Footer */}
                                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                                            <Clock className="w-3 h-3 text-slate-500" />
                                            <p className="text-xs text-slate-600">
                                              {new Date(note.created_at).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <StickyNote className="w-8 h-8 text-slate-400" />
                                  </div>
                                  <p className="text-slate-800 font-medium">No notes yet</p>
                                  <p className="text-slate-600 text-sm mt-1">Start capturing your insights below!</p>
                                </div>
                              )}

                              {/* New Note Section */}
                              <div className="space-y-3 pt-4 border-t-2 border-slate-200">
                                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                  <div className="w-1 h-4 bg-green-500 rounded-full"></div>
                                  Add New Note
                                </h3>
                                <div className="relative">
                                  <Textarea
                                    placeholder="Write your thoughts, key points, questions, or insights here..."
                                    value={currentNote}
                                    onChange={(e) => setCurrentNote(e.target.value)}
                                    className="min-h-[120px] bg-white border-2 border-slate-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg resize-none text-slate-800 placeholder:text-slate-400"
                                  />
                                  <div className="absolute bottom-3 right-3 text-xs text-slate-500 bg-white px-2 py-1 rounded">
                                    {currentNote.length} characters
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Footer with Action Button */}
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                              <div className="flex items-center gap-3">
                                <Button
                                  onClick={() => saveNote(topicId)}
                                  disabled={!currentNote.trim()}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11"
                                >
                                  <StickyNote className="w-4 h-4 mr-2" />
                                  Save Note
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setCurrentNote('')}
                                  disabled={!currentNote.trim()}
                                  className="px-4 h-11 border-slate-300 text-slate-700 hover:bg-slate-100"
                                >
                                  Clear
                                </Button>
                              </div>
                              <p className="text-xs text-slate-600 text-center mt-2">
                                💡 Tip: Write down key concepts, questions, or summaries
                              </p>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                    
                    {/* AI Tools Row */}
                    <div className="p-3 bg-slate-900/5 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] font-semibold text-white uppercase tracking-wide bg-slate-900 px-2 py-1 rounded">
                          AI Tools
                        </span>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateSummary(topicName, topicDescription)}
                          disabled={summaryLoading[topicName]}
                          className="text-xs h-8 px-3 bg-white border-slate-300 hover:border-slate-900 hover:bg-slate-100"
                        >
                          {summaryLoading[topicName] ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <FileText className="w-3.5 h-3.5 mr-1.5" />
                              Summary
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateWorksheet(topicName, topicDescription)}
                          disabled={worksheetLoading[topicName]}
                          className="text-xs h-8 px-3 bg-white border-slate-300 hover:border-slate-900 hover:bg-slate-100"
                        >
                          {worksheetLoading[topicName] ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
                              Worksheet
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateQuiz(topicId, topicName, topicDescription)}
                          disabled={quizLoading[topicName]}
                          className="text-xs h-8 px-3 bg-white border-slate-300 hover:border-slate-900 hover:bg-slate-100"
                        >
                          {quizLoading[topicName] ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <HelpCircle className="w-3.5 h-3.5 mr-1.5" />
                              Quiz
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateConceptMap(topicName, topicDescription)}
                          disabled={conceptMapLoading[topicName]}
                          className="text-xs h-8 px-3 bg-white border-slate-300 hover:border-slate-900 hover:bg-slate-100"
                        >
                          {conceptMapLoading[topicName] ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Network className="w-3.5 h-3.5 mr-1.5" />
                              Concept Map
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                  </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Summary Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white">
          {/* Header Section - Dark Blue like footer */}
          <div className="relative bg-slate-900 px-6 py-5">
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-white text-lg font-bold mb-1">
                    📚 Topic Summary
                  </DialogTitle>
                  <DialogDescription className="text-white/80 text-sm">
                    {currentSummary?.topicName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Content Section - Properly formatted markdown */}
          <div className="flex-1 overflow-y-auto px-6 py-4 bg-white">
            {currentSummary ? (
              <div className="space-y-4">
                {currentSummary.summary.split('\n').map((line, idx) => {
                  const trimmedLine = line.trim();
                  
                  // Skip empty lines but add spacing
                  if (!trimmedLine) {
                    return <div key={idx} className="h-2" />;
                  }
                  
                  // H1 headers (# )
                  if (trimmedLine.startsWith('# ')) {
                    return (
                      <h1 key={idx} className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-2 mt-4">
                        {trimmedLine.replace(/^# /, '')}
                      </h1>
                    );
                  }
                  
                  // H2 headers (## )
                  if (trimmedLine.startsWith('## ')) {
                    return (
                      <h2 key={idx} className="text-lg font-bold text-slate-800 mt-4 mb-2 flex items-center gap-2">
                        <div className="w-1 h-5 bg-slate-900 rounded-full"></div>
                        {trimmedLine.replace(/^## /, '')}
                      </h2>
                    );
                  }
                  
                  // H3 headers (### )
                  if (trimmedLine.startsWith('### ')) {
                    return (
                      <h3 key={idx} className="text-base font-semibold text-slate-700 mt-3 mb-1">
                        {trimmedLine.replace(/^### /, '')}
                      </h3>
                    );
                  }
                  
                  // Bullet points (- or * )
                  if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
                    const content = trimmedLine.replace(/^[-*] /, '');
                    // Parse bold text within bullet points
                    const parts = content.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div key={idx} className="flex items-start gap-2 ml-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-900 mt-2 flex-shrink-0"></div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  }
                  
                  // Numbered lists (1. 2. etc.)
                  const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
                  if (numberedMatch) {
                    const content = numberedMatch[2];
                    const parts = content.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div key={idx} className="flex items-start gap-2 ml-4">
                        <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {numberedMatch[1]}
                        </span>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  }
                  
                  // Regular paragraphs with bold text support
                  const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/g);
                  return (
                    <p key={idx} className="text-sm text-slate-700 leading-relaxed">
                      {parts.map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                      })}
                    </p>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-600 mx-auto mb-3" />
                <p className="text-slate-600">Generating summary...</p>
              </div>
            )}
          </div>

          {/* Footer with Download Button - Dark Blue */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <Button
                onClick={downloadSummary}
                disabled={!currentSummary}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Summary
              </Button>
              <Button
                variant="outline"
                onClick={() => setSummaryDialogOpen(false)}
                className="px-4 h-11 border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Close
              </Button>
            </div>
            <p className="text-xs text-slate-600 text-center mt-2">
              💡 Generated by FaheemGPT AI
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Worksheet Dialog */}
      <Dialog open={worksheetDialogOpen} onOpenChange={setWorksheetDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white">
          {/* Header Section - Dark Blue like footer */}
          <div className="relative bg-slate-900 px-6 py-5">
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-white text-lg font-bold mb-1">
                    📝 Practice Worksheet
                  </DialogTitle>
                  <DialogDescription className="text-white/80 text-sm">
                    {currentWorksheet?.topicName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Content Section - Properly formatted markdown */}
          <div className="flex-1 overflow-y-auto px-6 py-4 bg-white">
            {currentWorksheet ? (
              <div className="space-y-4">
                {currentWorksheet.worksheet.split('\n').map((line, idx) => {
                  const trimmedLine = line.trim();
                  
                  // Skip empty lines but add spacing
                  if (!trimmedLine) {
                    return <div key={idx} className="h-2" />;
                  }
                  
                  // H1 headers (# )
                  if (trimmedLine.startsWith('# ')) {
                    return (
                      <h1 key={idx} className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-2 mt-4">
                        {trimmedLine.replace(/^# /, '')}
                      </h1>
                    );
                  }
                  
                  // H2 headers (## )
                  if (trimmedLine.startsWith('## ')) {
                    return (
                      <h2 key={idx} className="text-lg font-bold text-slate-800 mt-6 mb-3 flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                        <div className="w-1.5 h-5 bg-slate-900 rounded-full"></div>
                        {trimmedLine.replace(/^## /, '')}
                      </h2>
                    );
                  }
                  
                  // H3 headers (### )
                  if (trimmedLine.startsWith('### ')) {
                    return (
                      <h3 key={idx} className="text-base font-semibold text-slate-700 mt-4 mb-2 border-l-4 border-slate-400 pl-3">
                        {trimmedLine.replace(/^### /, '')}
                      </h3>
                    );
                  }
                  
                  // Answer key marker
                  if (trimmedLine.toLowerCase().includes('answer key') || trimmedLine.toLowerCase().includes('answers')) {
                    if (trimmedLine.startsWith('#')) {
                      return (
                        <h2 key={idx} className="text-lg font-bold text-green-700 mt-8 mb-3 flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                          <CheckCircle2 className="w-5 h-5" />
                          {trimmedLine.replace(/^#+ /, '')}
                        </h2>
                      );
                    }
                  }
                  
                  // Bullet points (- or * )
                  if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
                    const content = trimmedLine.replace(/^[-*] /, '');
                    const parts = content.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div key={idx} className="flex items-start gap-2 ml-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-900 mt-2 flex-shrink-0"></div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  }
                  
                  // Numbered lists and questions (1. 2. A) B) etc.)
                  const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
                  const letterMatch = trimmedLine.match(/^([A-Da-d])\)\s+(.*)$/);
                  
                  if (numberedMatch) {
                    const content = numberedMatch[2];
                    const parts = content.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div key={idx} className="flex items-start gap-3 ml-2 my-2">
                        <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold">
                          {numberedMatch[1]}
                        </span>
                        <p className="text-sm text-slate-700 leading-relaxed flex-1">
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  }
                  
                  if (letterMatch) {
                    const content = letterMatch[2];
                    const parts = content.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div key={idx} className="flex items-start gap-2 ml-10 my-1">
                        <span className="w-5 h-5 rounded bg-slate-200 text-slate-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-medium">
                          {letterMatch[1].toUpperCase()}
                        </span>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  }
                  
                  // Regular paragraphs with bold text support
                  const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/g);
                  return (
                    <p key={idx} className="text-sm text-slate-700 leading-relaxed">
                      {parts.map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                      })}
                    </p>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-600 mx-auto mb-3" />
                <p className="text-slate-600">Generating worksheet...</p>
              </div>
            )}
          </div>

          {/* Footer with Download Button - Dark Blue */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <Button
                onClick={downloadWorksheet}
                disabled={!currentWorksheet}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Worksheet
              </Button>
              <Button
                variant="outline"
                onClick={() => setWorksheetDialogOpen(false)}
                className="px-4 h-11 border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Close
              </Button>
            </div>
            <p className="text-xs text-slate-600 text-center mt-2">
              📝 Practice Makes Perfect! Generated by FaheemGPT AI
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={quizDialogOpen} onOpenChange={(open) => !open && closeQuizDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white">
          {/* Header Section - Dark Blue */}
          <div className="relative bg-slate-900 px-6 py-5">
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-white text-lg font-bold mb-1">
                    📝 Topic Quiz
                  </DialogTitle>
                  <DialogDescription className="text-white/80 text-sm">
                    {currentQuiz?.topic_name}
                  </DialogDescription>
                </div>
                {quizSubmitted && quizResults && (
                  <div className="bg-white/20 rounded-lg px-4 py-2">
                    <div className="text-2xl font-bold text-white">{quizResults.percentage}%</div>
                    <div className="text-xs text-white/80">{quizResults.correct_count}/{quizResults.total_questions} correct</div>
                  </div>
                )}
              </div>
            </DialogHeader>
          </div>

          {/* Quiz Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 bg-white">
            {currentQuiz ? (
              <div className="space-y-6">
                {/* Progress indicator */}
                {!quizSubmitted && (
                  <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
                    <span>{currentQuiz.questions.filter((q, idx) => {
                      const answer = quizAnswers[idx];
                      const isOpenEnded = q.question_type === 'open_ended' || (q.options && q.options.length === 0);
                      if (isOpenEnded) return answer && typeof answer === 'string' && answer.trim() !== '';
                      return answer !== undefined;
                    }).length} of {currentQuiz.questions.length} answered</span>
                    <div className="flex-1 mx-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-slate-900 transition-all duration-300"
                        style={{ width: `${(currentQuiz.questions.filter((q, idx) => {
                          const answer = quizAnswers[idx];
                          const isOpenEnded = q.question_type === 'open_ended' || (q.options && q.options.length === 0);
                          if (isOpenEnded) return answer && typeof answer === 'string' && answer.trim() !== '';
                          return answer !== undefined;
                        }).length / currentQuiz.questions.length) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Questions */}
                {currentQuiz.questions.map((question, qIndex) => {
                  const isAnswered = quizAnswers[qIndex] !== undefined && quizAnswers[qIndex] !== '';
                  const selectedAnswer = quizAnswers[qIndex];
                  const result = quizResults?.results?.[qIndex];
                  const isCorrect = result?.is_correct;
                  const isOpenEnded = question.question_type === 'open_ended' || (question.options && question.options.length === 0);
                  
                  return (
                    <div 
                      key={question.id} 
                      className={`p-4 rounded-lg border-2 transition-all ${
                        quizSubmitted
                          ? isCorrect
                            ? 'border-green-200 bg-green-50'
                            : 'border-red-200 bg-red-50'
                          : isAnswered
                            ? 'border-slate-300 bg-slate-50'
                            : 'border-slate-200 bg-white'
                      }`}
                    >
                      {/* Question Header */}
                      <div className="flex items-start gap-3 mb-3">
                        <span className={`w-7 h-7 rounded-full text-white text-sm flex items-center justify-center flex-shrink-0 font-semibold ${
                          isOpenEnded ? 'bg-purple-600' : 'bg-slate-900'
                        }`}>
                          {qIndex + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-slate-800 font-medium">{question.question}</p>
                          {isOpenEnded && !quizSubmitted && (
                            <span className="inline-flex items-center gap-1 text-xs text-purple-600 mt-1">
                              <Sparkles className="w-3 h-3" />
                              Open-ended question - Write your answer below
                            </span>
                          )}
                        </div>
                        {quizSubmitted && (
                          isCorrect 
                            ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            : <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        )}
                      </div>

                      {/* Open-ended text input OR Multiple choice options */}
                      <div className="ml-10">
                        {isOpenEnded ? (
                          <div className="space-y-2">
                            <textarea
                              value={selectedAnswer || ''}
                              onChange={(e) => handleOpenEndedAnswer(qIndex, e.target.value)}
                              disabled={quizSubmitted}
                              placeholder="Type your answer here..."
                              className={`w-full p-3 rounded-lg border-2 text-sm resize-none min-h-[100px] ${
                                quizSubmitted 
                                  ? 'bg-slate-100 border-slate-200 cursor-not-allowed'
                                  : 'bg-white border-slate-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                              }`}
                              rows={4}
                            />
                            {quizSubmitted && result && (
                              <div className={`p-3 rounded-lg ${isCorrect ? 'bg-green-100 border border-green-200' : 'bg-amber-100 border border-amber-200'}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-sm font-semibold ${isCorrect ? 'text-green-700' : 'text-amber-700'}`}>
                                    AI Score: {result.score || 0}/100
                                  </span>
                                </div>
                                {result.feedback && (
                                  <p className="text-sm text-slate-600">{result.feedback}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {question.options.map((option, oIndex) => {
                              const isSelected = selectedAnswer === oIndex;
                              const isCorrectAnswer = question.correct_answer === oIndex;
                              
                              let optionClass = "flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ";
                              
                              if (quizSubmitted) {
                                if (isCorrectAnswer) {
                                  optionClass += "border-green-500 bg-green-100 ";
                                } else if (isSelected && !isCorrectAnswer) {
                                  optionClass += "border-red-500 bg-red-100 ";
                                } else {
                                  optionClass += "border-slate-200 bg-white opacity-60 ";
                                }
                              } else {
                                if (isSelected) {
                                  optionClass += "border-slate-900 bg-slate-100 ";
                                } else {
                                  optionClass += "border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 ";
                                }
                              }
                              
                              return (
                                <div
                                  key={oIndex}
                                  className={optionClass}
                                  onClick={() => handleQuizAnswer(qIndex, oIndex)}
                                >
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                                    quizSubmitted
                                      ? isCorrectAnswer
                                        ? 'bg-green-600 text-white'
                                        : isSelected
                                          ? 'bg-red-600 text-white'
                                          : 'bg-slate-300 text-slate-600'
                                      : isSelected
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-slate-200 text-slate-600'
                                  }`}>
                                    {String.fromCharCode(65 + oIndex)}
                                  </span>
                                  <span className={`text-sm ${
                                    quizSubmitted && isCorrectAnswer ? 'font-semibold text-green-800' : 'text-slate-700'
                                  }`}>
                                    {option}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-600 mx-auto mb-3" />
                <p className="text-slate-600">Loading quiz...</p>
              </div>
            )}
          </div>

          {/* Footer with Submit/Retake Buttons */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            {!quizSubmitted ? (
              <div className="flex items-center gap-3">
                <Button
                  onClick={submitQuiz}
                  disabled={quizSubmitting || !currentQuiz?.questions || currentQuiz.questions.some((q, idx) => {
                    const answer = quizAnswers[idx];
                    const isOpenEnded = q.question_type === 'open_ended' || (q.options && q.options.length === 0);
                    if (isOpenEnded) return !answer || (typeof answer === 'string' && answer.trim() === '');
                    return answer === undefined;
                  })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
                >
                  {quizSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Submit Quiz
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={closeQuizDialog}
                  className="px-4 h-11 border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Score Summary */}
                <div className={`text-center p-4 rounded-lg ${
                  quizResults?.percentage >= 80 
                    ? 'bg-green-100 border border-green-200' 
                    : quizResults?.percentage >= 60 
                      ? 'bg-yellow-100 border border-yellow-200'
                      : 'bg-red-100 border border-red-200'
                }`}>
                  <div className="text-3xl font-bold mb-1">
                    {quizResults?.percentage >= 80 ? '🎉' : quizResults?.percentage >= 60 ? '👍' : '📚'} {quizResults?.percentage}%
                  </div>
                  <p className="text-sm text-slate-600">
                    You got {quizResults?.correct_count} out of {quizResults?.total_questions} questions correct
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <Button
                    onClick={retakeQuiz}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Retake Quiz
                  </Button>
                  <Button
                    variant="outline"
                    onClick={closeQuizDialog}
                    className="px-4 h-11 border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-600 text-center mt-2">
              🧠 Test your knowledge! Generated by FaheemGPT AI
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Concept Map Dialog */}
      <ConceptMapDialog
        open={conceptMapDialogOpen}
        onOpenChange={setConceptMapDialogOpen}
        conceptMap={currentConceptMap}
        topicName={currentConceptMapTopic}
      />
    </div>
  );
}
